import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import AppNav from "../components/appNav";
import Footer from "../components/footer";
import { ArrowRightIcon } from "@heroicons/react/24/outline";

const moduleKeys = [
  { key: "Authentication & Wallet Module", body: "Web3Auth integration with social login (Google, Apple, email). Automatic non-custodial wallet generation using Shamir's Secret Sharing. Multi-factor authentication with biometric support. Session management across web and mobile. Zero private key exposure to users. Wallet recovery without seed phrases through social authentication providers." },
  { key: "Token Engine", body: "ERC-20 loyalty tokens with configurable economics. Automatic minting on purchase events triggered by POS integration. Burn mechanism for redemptions with transaction receipts. Rate limiting and daily earning caps. Cross-chain bridge functionality for multi-location businesses. Gas optimization with meta-transactions to eliminate user fees. Admin controls for token supply, circulation limits, and emergency pause functionality." },
  { key: "Loyalty Logic Engine", body: "Configurable earn rules: percentage-based rewards, fixed point amounts, tiered rates by customer level, bonus multipliers for specific products. Redemption catalog management with real-time inventory tracking. Campaign system for limited-time bonuses and seasonal promotions. Tier progression with spending thresholds and time-based qualifications. Referral reward automation with fraud detection. Analytics dashboard for customer behavior patterns and campaign performance." },
  { key: "NFT Membership System", body: "ERC-721 membership cards with dynamic metadata updates for tier changes. ERC-1155 achievement badges with rarity systems and collection mechanics. Utility integration for real-world perks: room upgrades, exclusive access, priority service. Marketplace functionality for secondary trading of rare achievements. Staking mechanisms for additional reward generation. Custom artwork and branding integration with metadata hosting on IPFS." },
  { key: "Gamification Engine", body: "Visit streak tracking with blockchain verification and reset protection. Challenge system with completion criteria and time limits. Leaderboard rankings with privacy controls and seasonal resets. Progress bars for milestone tracking with visual feedback. Achievement unlocks triggering NFT minting. Social features for sharing accomplishments. Quest systems with multi-step objectives and collaborative challenges." },
  { key: "QR Redemption Network", body: "Dynamic QR code generation with encrypted transaction data. Merchant scanning application with offline capability. Real-time balance verification and deduction processing. Receipt generation with blockchain transaction IDs. POS integration APIs for major providers (Square, Shopify, Toast, Lightspeed). Bulk redemption processing for large transactions. Refund mechanisms with automated point restoration." },
  { key: "Mobile SDK & Applications", body: "React Native and Flutter SDKs for rapid deployment. Pre-built UI components with customizable branding. Native device integration: camera scanning, push notifications, biometric authentication. Offline transaction queuing with automatic sync. Deep linking for marketing campaigns. White-label application deployment with custom app store submissions." },
  { key: "Admin Dashboard", body: "Campaign management with targeting rules and budget controls. Customer analytics with lifetime value calculations and segmentation tools. Token and NFT management with minting controls and metadata updates. Integration monitoring with API health checks and error reporting. User permission system with role-based access controls. Bulk operations for customer imports and reward distributions." },
  { key: "Integration Hub", body: "REST API with comprehensive documentation and SDKs. Webhook system for real-time event notifications. Pre-built connectors for CRM systems (Salesforce, HubSpot). Analytics platform integration (Mixpanel, Google Analytics). Email marketing automation (Mailchimp, SendGrid). Custom integration development for enterprise requirements." },
  { key: "Blockchain Infrastructure", body: "Multi-chain deployment supporting Camino Network, Polygon, Base, Arbitrum, and Ethereum mainnet. Gas optimization through batch transactions and layer-2 scaling. Node management with failover redundancy and load balancing. Transaction monitoring with automatic retry mechanisms. Smart contract upgradeability through proxy patterns. Security audits and formal verification of core contracts." },
];

const specs = [
  { title: "Performance", body: "Polygon network limitations apply (2-3 second block times). Transaction throughput depends on network congestion. We batch operations where possible but can't control blockchain speed." },
  { title: "Security", body: "Smart contracts audited by independent firms. Standard Web3 security practices. No guarantees against blockchain-level vulnerabilities." },
  { title: "Compliance", body: "Basic GDPR compliance for off-chain data. Blockchain data is permanent and can't be deleted. Legal complexity around token classification varies by jurisdiction." },
  { title: "Scalability", body: "Database scales normally. Blockchain operations don't scale like traditional software. Layer-2 helps but adds complexity." },
];

const InnovationPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="sticky top-0 z-50 bg-paper/95 backdrop-blur-[2px] border-b border-ink-14">
        <AppNav />
      </header>

      <main>
        {/* 01 / Hero */}
        <section className="swiss-section !border-t-0 pt-7 lg:pt-9">
          <div className="swiss-grid">
            <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
              <span className="swiss-num">01 / {t("Infrastructure")}</span>
            </div>
            <div className="col-span-12 lg:col-span-10">
              <h1 className="text-[clamp(2.5rem,5.5vw,5.5rem)] font-black tracking-tightest leading-[0.95] text-ink max-w-[18ch] mb-7">
                {t("On-chain loyalty infrastructure.")}
              </h1>

              <Link
                to="/bogowi"
                className="group inline-flex items-center justify-between gap-6 border border-ink pl-5 pr-4 py-4 max-w-2xl w-full mb-7 hover:bg-ink hover:text-paper transition-colors"
              >
                <div className="flex items-baseline gap-4">
                  <span className="swiss-caption text-ink-50 group-hover:text-paper">FEATURED</span>
                  <span className="text-h4 font-medium tracking-tight">BOGOWI</span>
                </div>
                <ArrowRightIcon className="h-5 w-5" />
              </Link>

              <p className="text-body-lg text-ink-70 max-w-prose">
                KODE provides blockchain-powered customer engagement infrastructure that turns
                traditional loyalty programs into permanent, fraud-proof reward systems.
              </p>
            </div>
          </div>
        </section>

        {/* 02 / Overview */}
        <section className="swiss-section">
          <div className="swiss-grid">
            <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
              <span className="swiss-num">02 / {t("Overview")}</span>
            </div>
            <div className="col-span-12 lg:col-span-10 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
              <p className="text-body text-ink-70 max-w-prose">
                Blockchain-powered customer engagement infrastructure that turns traditional loyalty
                programs into permanent, fraud-proof reward systems. Our modular platform enables
                businesses to deploy Web3 loyalty solutions without blockchain complexity.
              </p>
              <p className="text-body text-ink-70 max-w-prose">
                The system transforms customer interactions into blockchain transactions, creating
                tamper-proof point balances that never expire. Customers use familiar interfaces while
                businesses gain unprecedented control over reward distribution and data ownership.
              </p>
            </div>
          </div>
        </section>

        {/* 03 / Modules */}
        <section className="swiss-section">
          <div className="swiss-grid">
            <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
              <span className="swiss-num">03 / {t("Modules")}</span>
            </div>
            <div className="col-span-12 lg:col-span-10">
              <h2 className="text-h2 font-bold tracking-tight text-ink mb-9 max-w-[18ch]">
                Core system architecture.
              </h2>
              <ul className="border-b border-ink-14">
                {moduleKeys.map(({ key, body }, i) => (
                  <li
                    key={key}
                    className="grid grid-cols-12 gap-x-6 gap-y-3 border-t border-ink-14 py-5"
                  >
                    <div className="col-span-12 md:col-span-1 swiss-num text-ink-50">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <h4 className="col-span-12 md:col-span-3 text-h4 font-medium tracking-tight text-ink">
                      {key}
                    </h4>
                    <p className="col-span-12 md:col-span-8 text-body text-ink-70 max-w-prose">
                      {body}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 04 / Specifications */}
        <section className="swiss-section">
          <div className="swiss-grid">
            <div className="col-span-12 lg:col-span-2 mb-6 lg:mb-0">
              <span className="swiss-num">04 / {t("Specifications")}</span>
            </div>
            <div className="col-span-12 lg:col-span-10">
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {specs.map((s) => (
                  <li key={s.title} className="border-t border-ink pt-4">
                    <h4 className="text-h4 font-medium tracking-tight text-ink mb-3">{s.title}</h4>
                    <p className="text-body text-ink-70 max-w-measure">{s.body}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default InnovationPage;
